import { useAuth } from "@dl/web-core-js/auth";
import { ResourceClient } from "@dl/web-core-js/api";
import { columns as defineColumns, DataTable, type RowEditorConfig } from "@dl/web-core-js/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ProductMetafieldClient,
  type MetafieldDTO,
  type ProductDTO,
  type ProductMetafieldDTO,
} from "../../api/product";

/**
 * Typed custom fields on the product.
 *
 * A proper sub-resource with its own CRUD endpoints, so this reuses the shared
 * table's inline editor rather than buffering into the page draft — each row
 * saves against `/product/metafield` directly, as the Vaadin grid did.
 */
export function MetafieldsTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();

  const client = useMemo(() => new ProductMetafieldClient(http), [http]);
  const definitionsClient = useMemo(
    () => new ResourceClient<MetafieldDTO>(http, "/metafield"),
    [http],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["product", "metafields", draft.id],
    queryFn: () => client.byProduct(draft.id),
  });

  const { data: definitions } = useQuery({
    queryKey: ["reference", "metafield"],
    queryFn: () => definitionsClient.findAll(),
    staleTime: 30 * 60_000,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["product", "metafields", draft.id] });

  const rows = data ?? [];
  const available = definitions ?? [];

  const cols = useMemo(() => {
    const c = defineColumns<ProductMetafieldDTO>();
    return [
      c.text("name", (row) => row.metafield?.name ?? "", { headerKey: "column.name", priority: 1 }),
      c.text("value", (row) => row.value ?? "", { headerKey: "column.value", priority: 2 }),
    ];
  }, []);

  const editor = useMemo<RowEditorConfig<ProductMetafieldDTO>>(
    () => ({
      fields: {
        name: {
          kind: "select",
          get: (row) => (row.metafield ? String(row.metafield.id) : ""),
          set: (row, value) => ({
            ...row,
            metafield: available.find((definition) => String(definition.id) === value) ?? row.metafield,
          }),
          options: available.map((definition) => ({
            value: String(definition.id),
            label: definition.name,
          })),
          required: true,
        },
        value: {
          kind: "text",
          get: (row) => row.value,
          set: (row, value) => ({ ...row, value }),
          required: true,
        },
      },
      newRow: () => ({
        id: 0,
        product_id: draft.id,
        metafield: available[0] ?? { id: 0, name: "" },
        value: "",
      }),
      validate: (row, isNew): Record<string, string> => {
        const errors: Record<string, string> = {};
        if (!row.value.trim()) {
          errors.value = t("form.validation.field.required", "Required");
        }
        // One value per metafield: the same key twice would be ambiguous in
        // every feed that consumes these.
        const duplicate = rows.some(
          (other) => other.metafield?.id === row.metafield?.id && (isNew || other.id !== row.id),
        );
        if (duplicate) {
          errors.name = t("form.validation.field.unique", "Already in use");
        }
        return errors;
      },
      onSave: async (row, isNew) => {
        if (isNew) await client.create(row);
        else await client.update(row);
        await refresh();
        toast.success(t("notification.saved", "Saved"));
      },
      onDelete: async (row) => {
        await client.remove(row.id);
        await refresh();
        toast.success(t("notification.deleted", "Deleted"));
      },
      deleteConfirm: (row) => ({
        title: t("product.metafields.delete.title", "Delete this metafield?"),
        message: row.metafield?.name ?? "",
      }),
    }),
    [available, rows, client, draft.id, t, queryClient],
  );

  return (
    <DataTable
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={isLoading}
      editor={editor}
      emptyMessage={t("product.metafields.none", "No metafields on this product.")}
    />
  );
}
